import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReportsUtilsService } from '@app/modules/reports/services/reports-utils.service';
import { ResultsUtilsService } from '@app/modules/reports/services/results-utils.service';
import { GetReportsListAction } from '@app/modules/reports/store/reports.actions';
import { GetImportAction } from '@app/modules/reports/store/result-import.actions';
import { ResultImportState } from '@app/modules/reports/store/result-import.state';
import { GetResultsListAction } from '@app/modules/reports/store/results.actions';
import { ResultsState } from '@app/modules/reports/store/results.state';
import {
  isLowRiskReport,
  ReportRisk,
  ReportStatus,
  ReportType,
} from '@app/modules/reports/types/report.types';
import { ExtendedResult } from '@app/modules/reports/types/result.types';
import { Run, SortDirection } from '@core/core.types';
import { ProductsListState } from '@core/store/products/products.state';
import { RunState } from '@core/store/runs/runs.state';
import { SigneeState } from '@core/store/signees/signees.state';
import { Util } from '@core/utils/core.util';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableActionType,
  DataTableConfiguration,
  DataTableFilterType,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import {
  NavTab,
  NavTabsetComponent,
} from '@shared/components/nav-tabset/nav-tabset.component';
import { UserDisplayComponent } from '@shared/components/user-display/user-display.component';
import { groupBy } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { CustomReportsFormComponent } from '../../reports/custom-reports-form/custom-reports-form.component';

interface CalculatedSummary {
  generatedReportsCount: number;
  failedResultsCount: number;
  highRiskReportsCount: number;
  lowRiskReportsCount: number;
  reportCountByType: {
    [key: string]: number;
  };
  joinedRuns: string;
}
@Component({
  selector: 'app-results-import-summary-dialog',
  imports: [
    TranslatePipe,
    DataTableComponent,
    DatePipe,
    UserDisplayComponent,
    NavTabsetComponent,
  ],
  templateUrl: './results-import-summary-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './results-import-summary-dialog.component.scss',
})
export class ResultsImportSummaryDialogComponent implements OnInit {
  private modal = inject(NgbActiveModal);
  private store = inject(Store);
  private reportsUtilsService = inject(ReportsUtilsService);
  private resultsUtilsService = inject(ResultsUtilsService);
  private translateService = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private ngbModal = inject(NgbModal);

  public importId = signal<string>('');

  protected validDataTableConfiguration: DataTableConfiguration;
  protected invalidDataTableConfiguration: DataTableConfiguration;
  protected errorsDataTableConfiguration: DataTableConfiguration;

  protected productById = this.store.selectSignal(ProductsListState.getProduct);
  protected signeeById = this.store.selectSignal(SigneeState.getSigneeById);
  protected runsByKitId = this.store.selectSignal(RunState.getRunsByKitId);
  protected errors = this.store.selectSignal(ResultImportState.getErrors);
  protected import = computed(() => {
    return this.store.selectSignal(ResultImportState.getImport)()(
      this.importId()
    );
  });
  protected results = computed(() => {
    return this.store.selectSignal(ResultImportState.getResults)()(
      this.import().rawDataIds
    );
  });
  protected isLoading = signal(true);
  protected errors$ = toObservable(this.errors);
  protected statusTemplate = viewChild<TemplateRef<any>>('statusTemplate');
  protected reportedRiskTemplate = viewChild<TemplateRef<any>>(
    'reportedRiskTemplate'
  );
  protected detectedRiskTemplate = viewChild<TemplateRef<any>>(
    'detectedRiskTemplate'
  );
  protected failedReasonTemplate = viewChild<TemplateRef<any>>(
    'failedReasonTemplate'
  );
  protected errorTemplate = viewChild<TemplateRef<any>>('errorTemplate');
  protected validResultsTemplate = viewChild<TemplateRef<any>>(
    'validResultsTemplate'
  );
  protected invalidResultsTemplate = viewChild<TemplateRef<any>>(
    'invalidResultsTemplate'
  );

  protected tabs: NavTab[] = [
    {
      title: this.translateService.instant('results.import.resultsTitle', {
        count: 0,
      }),
      icon: 'fa-light fa-check',
      template: this.validResultsTemplate,
    },
    {
      title: this.translateService.instant(
        'results.import.failedResultsTitle',
        { count: 0 }
      ),
      icon: 'fa-light fa-times',
      template: this.invalidResultsTemplate,
    },
  ];

  protected summary = computed(() => {
    return {
      generatedReportsCount: this.getGeneratedReportsCount(),
      failedResultsCount: this.getFailedResultsCount(),
      highRiskReportsCount: this.getHighRiskReportsCount(),
      lowRiskReportsCount: this.getLowRiskReportsCount(),
      reportCountByType: this.getReportsByType(),
      joinedRuns: this.getAllJoinedRuns(),
    };
  });
  private validPage$ = new BehaviorSubject<number>(0);
  private validPageSize$ = new BehaviorSubject<number>(30);
  private invalidPage$ = new BehaviorSubject<number>(0);
  private invalidPageSize$ = new BehaviorSubject<number>(30);
  protected reportTypeFilter$ = new BehaviorSubject<ReportType>(null);
  protected statusFilter$ = new BehaviorSubject<ReportStatus>(null);
  protected reportedRiskFilter$ = new BehaviorSubject<ReportRisk>(null);
  protected detectedRiskFilter$ = new BehaviorSubject<ReportRisk>(null);

  ngOnInit(): void {
    const results$ = this.store
      .dispatch(new GetImportAction(this.importId()))
      .pipe(
        switchMap(() =>
          this.store
            .select(ResultImportState.getImport)
            .pipe(map((getImport) => getImport(this.importId()).rawDataIds))
        ),
        take(1),
        switchMap((rawDataIds: string[]) =>
          this.store
            .dispatch(
              new GetResultsListAction({ ids: rawDataIds }, {}, null, true)
            )
            .pipe(
              switchMap(() => this.store.select(ResultsState.getResultsByIds)),
              map((getResultsByIds) =>
                getResultsByIds(rawDataIds)
                  .map((result) => result.reportIds)
                  .flat(1)
              ),
              take(1),
              switchMap((ids: string[]) =>
                ids?.length
                  ? this.store.dispatch(
                      new GetReportsListAction({ ids: ids }, {}, null, true)
                    )
                  : of(null)
              ),
              switchMap(() =>
                this.store
                  .select(ResultImportState.getResults)
                  .pipe(map((getResults) => getResults(rawDataIds)))
              ),
              takeUntilDestroyed(this.destroyRef),
              map((results: ExtendedResult[]) => ({
                valid: results.filter(
                  (result: ExtendedResult) => !!result.report
                ),
                invalid: results.filter(
                  (result: ExtendedResult) => !result.report
                ),
              })),
              tap((results) => {
                this.tabs[0].title = this.translateService.instant(
                  'results.import.resultsTitle',
                  { count: results.valid.length }
                );
                this.tabs[1].title = this.translateService.instant(
                  'results.import.failedResultsTitle',
                  { count: results.invalid.length }
                );
              })
            )
        ),

        tap(() => this.isLoading.set(false)),
        shareReplay(1)
      );

    this.validDataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.MULTI,
      actionsClasses: 'w-15 text-end',
      actionsLocation: DataTableActionsLocation.RIGHT,
      pagination$: combineLatest([
        results$,
        this.validPageSize$,
        this.validPage$,
        this.reportTypeFilter$,
        this.statusFilter$,
        this.reportedRiskFilter$,
        this.detectedRiskFilter$,
      ]).pipe(
        map(
          ([
            results,
            pageSize,
            page,
            reportTypeFilter,
            statusFilter,
            reportedRiskFilter,
            detectedRiskFilter,
          ]) => ({
            totalCount: this.getFilters(
              results.valid,
              reportTypeFilter,
              statusFilter,
              reportedRiskFilter,
              detectedRiskFilter
            ).length,
            pagesSize: 9999,
            collectionSize: pageSize,
            currentPageSettings: {
              pageIndex: page,
              sortSettings: {
                orderBy: '',
                sortDirection: SortDirection.asc,
                pageSize,
              },
              filterSettings: null,
            },
          })
        )
      ),
      singleActions: [
        {
          type: DataTableActionType.BUTTON,
          name: 'results.import.actions.viewReportDetails',
          icon: 'fa-light fa-file',
          invisible: (result: ExtendedResult) =>
            result.report.status === ReportStatus.Invalid,
          action: (event: MouseEvent, result: ExtendedResult) =>
            this.reportsUtilsService.openReportDetailsDialog(result.report.id),
          permission: 'reports.reportsList.buttons.view',
        },
        {
          type: DataTableActionType.BUTTON,
          name: 'results.import.actions.viewResultDetails',
          icon: 'fa-light fa-table-list',
          action: (event: MouseEvent, result: ExtendedResult) =>
            this.resultsUtilsService.openResultDetails(result.id),
        },
        {
          type: DataTableActionType.BUTTON,
          name: 'results.import.actions.editReport',
          icon: 'fa-light fa-pencil',
          invisible: (result: ExtendedResult) =>
            result.report.status !== ReportStatus.Invalid,
          action: (event: MouseEvent, result: ExtendedResult) =>
            this.reportsUtilsService.openEditReportDialog(result.report.id),
          permission: 'reports.reportsList.buttons.manage',
        },
        {
          type: DataTableActionType.BUTTON,
          name: 'results.import.actions.archiveReport',
          icon: 'fa-light fa-archive',
          disabled: (result: ExtendedResult) =>
            result.report.status == ReportStatus.Archived ||
            result.report.status == ReportStatus.Approved,
          action: (event: MouseEvent, result: ExtendedResult) =>
            this.reportsUtilsService.archiveReports([result.report]),
          permission: 'reports.reportsList.buttons.manage',
        },
      ],
      massActions: [
        {
          type: DataTableActionType.BUTTON,
          name: 'pii.list.buttons.generateCustomReport',
          icon: 'fa-light fa-file-lines',
          permission: 'pii.list.buttons.reports',
          action: this.generateCustomReports.bind(this),
          disabled: (results: ExtendedResult[]) => !results.length,
        },
      ],
      columns: [
        {
          label: 'results.import.columns.sampleId',
          property: 'kitId',
        },
        {
          label: 'results.import.columns.detectedRisk',
          propertyTemplate: this.detectedRiskTemplate(),
          filter: {
            translationPrefix: 'reports.risk.',
            type: DataTableFilterType.SELECT,
            subject: this.detectedRiskFilter$,
            options: of(
              Object.values(ReportRisk).map((value) => ({
                value,
              }))
            ),
            nameProperty: 'value',
            idProperty: 'value',
          },
        },
        {
          label: 'results.import.columns.reportedRisk',
          propertyTemplate: this.reportedRiskTemplate(),
          filter: {
            translationPrefix: 'reports.risk.',
            type: DataTableFilterType.SELECT,
            subject: this.reportedRiskFilter$,
            options: of(
              Object.values(ReportRisk).map((value) => ({
                value,
              }))
            ),
            nameProperty: 'value',
            idProperty: 'value',
          },
        },
        {
          label: 'results.import.columns.product',
          invoke: {
            objectReference: {
              fn: (result: ExtendedResult) =>
                this.productById()(
                  Util.PropertiesToObject(result.report?.properties)?.productid
                )?.internalName ?? '/',
            },
            name: 'fn',
            args: [],
          },
        },
        {
          label: 'results.import.columns.reportType',
          property: 'report.reportType',
          filter: {
            type: DataTableFilterType.SELECT,
            subject: this.reportTypeFilter$,
            options: of(
              Object.values(ReportType).map((value) => ({
                value,
              }))
            ),
            nameProperty: 'value',
            idProperty: 'value',
          },
        },
        {
          label: 'results.import.columns.status',
          propertyTemplate: this.statusTemplate(),
          filter: {
            type: DataTableFilterType.SELECT,
            subject: this.statusFilter$,
            options: of(
              Object.values(ReportStatus).map((value) => ({
                value,
              }))
            ),
            nameProperty: 'value',
            idProperty: 'value',
          },
        },
      ],
      rows$: combineLatest([
        results$,
        this.validPageSize$,
        this.validPage$,
        this.reportTypeFilter$,
        this.statusFilter$,
        this.reportedRiskFilter$,
        this.detectedRiskFilter$,
      ]).pipe(
        map(
          ([
            results,
            pageSize,
            page,
            reportTypeFilter,
            statusFilter,
            reportedRiskFilter,
            detectedRiskFilter,
          ]) => {
            const filteredResults = this.getFilters(
              results.valid,
              reportTypeFilter,
              statusFilter,
              reportedRiskFilter,
              detectedRiskFilter
            );
            const start = page * pageSize;
            const end = start + pageSize;
            return filteredResults.slice(start, end);
          }
        )
      ),
    };

    this.invalidDataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      pagination$: combineLatest([
        results$,
        this.invalidPageSize$,
        this.invalidPage$,
      ]).pipe(
        map(([results, pageSize, page]) => ({
          totalCount: results.invalid.length,
          pagesSize: 9999,
          collectionSize: pageSize,
          currentPageSettings: {
            pageIndex: page,
            sortSettings: {
              orderBy: '',
              sortDirection: SortDirection.asc,
              pageSize,
            },
            filterSettings: null,
          },
        }))
      ),
      columns: [
        {
          label: 'results.import.columns.fileName',
          invoke: {
            objectReference: {
              fn: (result: ExtendedResult) =>
                result.fileUrl.split('/').pop() ?? '/',
            },
            name: 'fn',
            args: [],
          },
        },
        {
          label: 'results.import.columns.sampleId',
          property: 'kitId',
        },
        {
          label: 'results.import.columns.signee',
          invoke: {
            objectReference: {
              fn: (result: ExtendedResult) =>
                this.signeeById()(result.signeeId)?.name ?? '/',
            },
            name: 'fn',
            args: [],
          },
        },
        {
          label: 'results.import.columns.failedReason',
          propertyTemplate: this.failedReasonTemplate(),
        },
      ],
      rows$: combineLatest([
        results$,
        this.invalidPageSize$,
        this.invalidPage$,
      ]).pipe(
        map(([results, pageSize, page]) => {
          const start = page * pageSize;
          const end = start + pageSize;
          return results.invalid.slice(start, end);
        })
      ),
    };

    this.errorsDataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      columns: [
        {
          label: 'Error',
          propertyTemplate: this.errorTemplate(),
        },
      ],
      rows$: this.errors$,
    };
  }

  public generateCustomReports(event: MouseEvent, data: ExtendedResult[]) {
    event.preventDefault();

    const piiIds = data.map((result) => result.report?.productInventoryItemId);

    const modalRef = this.ngbModal.open(CustomReportsFormComponent, {
      centered: true,
      modalDialogClass: 'modal-size-md',
    });

    modalRef.componentInstance.piiIds = piiIds;
  }

  protected dismiss() {
    this.modal.dismiss();
  }

  protected getFailedResultsCount() {
    return this.results().filter((result: ExtendedResult) => !result.report)
      .length;
  }

  protected getGeneratedReportsCount() {
    return this.results().filter((result: ExtendedResult) => !!result.report)
      .length;
  }

  protected getReportsByType() {
    return groupBy(
      this.results().filter((result: ExtendedResult) => !!result.report),
      'report.reportType'
    );
  }

  protected getLowRiskReportsCount() {
    return this.results().filter(
      (result: ExtendedResult) =>
        !!result.report &&
        isLowRiskReport(result.report.reportedRisk) &&
        result.report.reportType === ReportType.Official
    ).length;
  }

  protected getHighRiskReportsCount() {
    return this.results().filter(
      (result: ExtendedResult) =>
        !!result.report &&
        !isLowRiskReport(result.report.reportedRisk) &&
        result.report.reportType === ReportType.Official
    ).length;
  }

  protected getAllJoinedRuns() {
    return [
      ...new Set(
        this.results()
          .map((result: ExtendedResult) => this.runsByKitId()(result.kitId))
          .map((runs: Run[]) => runs.map((run) => run.name))
          .flat(1)
      ),
    ].join(', ');
  }

  protected getFileName() {
    return this.results()[0].fileUrl.split('/').pop() ?? '/';
  }

  protected getSignee() {
    return this.signeeById()(this.results()[0].signeeId)?.name ?? '/';
  }

  protected validOnPageChanges(selectedPage) {
    this.validPage$.next(selectedPage - 1);
  }

  protected validOnPageSizeChanges(selectedPageSize) {
    this.validPageSize$.next(selectedPageSize);
  }

  protected invalidOnPageChanges(selectedPage) {
    this.invalidPage$.next(selectedPage - 1);
  }

  protected invalidOnPageSizeChanges(selectedPageSize) {
    this.invalidPageSize$.next(selectedPageSize);
  }

  private getFilters(
    results: ExtendedResult[],
    reportTypeFilter: ReportType,
    statusFilter: ReportStatus,
    reportedRiskFilter: ReportRisk,
    detectedRiskFilter: ReportRisk
  ) {
    return results
      .filter((result: ExtendedResult) =>
        reportTypeFilter ? result.report.reportType === reportTypeFilter : true
      )
      .filter((result: ExtendedResult) =>
        statusFilter ? result.report.status === statusFilter : true
      )
      .filter((result: ExtendedResult) =>
        reportedRiskFilter
          ? result.report.reportedRisk === reportedRiskFilter
          : true
      )
      .filter((result: ExtendedResult) =>
        detectedRiskFilter ? result.detectedRisk === detectedRiskFilter : true
      );
  }

  protected readonly ReportStatus = ReportStatus;
  protected readonly ReportRisk = ReportRisk;
  protected readonly Object = Object;
  protected readonly isLowRiskReport = isLowRiskReport;
}
